import { useState } from "react";
import { CheckCircle2, Download, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ForwarderLink } from "@/components/sms/ForwarderLink";
import { fmtTs } from "@/components/sms/smsUtils";

const APK = "https://github.com/capcom6/android-sms-gateway/releases/latest";
const input = "h-10 px-3 rounded-lg border border-[#E0DFD6] bg-[#FAFAF6] text-sm w-full font-mono focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30";

const Step = ({ n, title, children }) => (
  <li className="flex gap-2.5">
    <span className="w-6 h-6 shrink-0 rounded-full bg-[#385C4B] text-white text-xs grid place-items-center font-semibold">{n}</span>
    <div className="flex-1 min-w-0 space-y-2 text-sm text-[#3A3A35]">
      <div className="font-semibold">{title}</div>
      {children}
    </div>
  </li>
);

const TestBox = ({ sms }) => (
  <div className={`rounded-lg border px-3 py-2 text-xs ${sms ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-[#FAFAF6] border-[#E0DFD6] text-[#6B6B61]"}`} data-testid="sms-phone-test-box">
    {sms ? (
      <span className="flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 shrink-0" />
        <span><b>Test SMS mil gaya!</b> {sms.from} · {fmtTs(sms.ts)}<br /><span className="break-words">{sms.text}</span></span>
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> SMS ka intezaar… (yahan apne aap dikh jayega)</span>
    )}
  </div>
);

export const PhoneConnectDialog = ({ open, phone, base, onClose, onDone }) => {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const test = phone?.last_sms && (phone.last_sms_ts || 0) > base ? phone.last_sms : null;
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sms/phone/connect", { username: user, password: pass, base_url: BACKEND_URL });
      toast.success(`Phone connected · ${data.device_name}`);
      setPass("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="sms-phone-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#385C4B]" /> Phone Connect — ek baar ka setup</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#6B6B61]">Phone pe SMS aate hi internet (data / WiFi) se seedha yahan aa jayega. Net band ho to phone SMS rok ke rakhta hai, net aate hi bhej deta hai.</p>
        <ol className="space-y-5">
          <Step n={1} title="Phone me app install karo">
            <p>Jis Android phone me SMS aate hain, usme <b>SMS Gateway for Android</b> app install karo (free).</p>
            <a href={APK} target="_blank" rel="noreferrer" className="h-8 px-3 rounded-full bg-[#385C4B] text-white text-xs font-semibold inline-flex items-center gap-1.5" data-testid="sms-phone-apk-link">
              <Download className="w-3.5 h-3.5" /> App download (APK)
            </a>
            <p className="text-[11px] text-[#6B6B61]">Link phone me kholo → sabse upar wali <b>.apk</b> file download karke install karo.</p>
          </Step>
          <Step n={2} title="App chalu karo">
            <p>App kholo → <b>SMS permission Allow</b> karo → Home me <b>Cloud server</b> ON → <b>Start</b> dabao. Ab Home pe <b>Username</b> aur <b>Password</b> dikhega.</p>
          </Step>
          <Step n={3} title="Username + Password yahan daalo">
            {phone?.connected_ts && (
              <p className="text-xs text-emerald-800 font-semibold inline-flex items-center gap-1" data-testid="sms-phone-connected-chip">
                <CheckCircle2 className="w-4 h-4" /> Connected · {phone.device_name}
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="Username" className={input} data-testid="sms-phone-username" autoComplete="off" />
              <input value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Password" type="password" className={input} data-testid="sms-phone-password" autoComplete="new-password" />
            </div>
            <button onClick={connect} disabled={busy || !user.trim() || !pass.trim()} data-testid="sms-phone-submit"
              className="h-9 px-5 rounded-full bg-[#385C4B] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform active:scale-95">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Connect phone
            </button>
          </Step>
          <Step n={4} title="24x7 chalu rakho">
            <p>Phone Settings → Apps → SMS Gateway → Battery → <b>Unrestricted / No restriction</b>. Xiaomi / Vivo / Oppo me <b>Auto-start ON</b>.</p>
            <p>App ke <b>Settings → Ping</b> ON karo (5 min) — isse yahan “Phone signal” dikhta rahega.</p>
          </Step>
          <Step n={5} title="Test karo">
            <p>Dusre phone se is phone pe koi bhi SMS bhejo.</p>
            <TestBox sms={test} />
          </Step>
        </ol>
        <ForwarderLink title="Dusra forwarder app? Manual link" />
      </DialogContent>
    </Dialog>
  );
};
